/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/
import { merge } from '@adobe-commerce/elsie/lib';
import { config } from '@/plp/api/initialize/initialize';
import { Product } from '../models/product';

const getCurrency = (currency?: string) => {
  if (!currency || !Intl.supportedValuesOf("currency").includes(currency)) {
    return 'USD';
  }
  return currency;
};

export const transformProduct = (data: any): Product | null => {
  // check if data is null or undefined, and if so return an empty object
  if (!data) {
    return {
      id: '',
      name: '',
      sku: '',
      shortDescription: '',
      url: '',
      urlKey: '',
      metaTitle: '',
      metaKeywords: '',
      metaDescription: '',
      lowStock: false,
      links: [],
      attributes: [],
      images: [],
      description: '',
      externalId: '',
      inputOptions: [],
      addToCartAllowed: false,
      price: undefined,
      priceRange: undefined,
      inStock: false,
      typename: '',
    };
  }

  const transformedProduct = {
    id: data?.id || '',
    name: data?.name || '',
    sku: data?.sku || '',
    shortDescription: data?.shortDescription || '',
    url: data?.url || '',
    urlKey: data?.urlKey || '',
    metaTitle: data?.metaTitle || '',
    metaKeywords: data?.metaKeywords || '',
    metaDescription: data?.metaDescription || '',
    lowStock: data?.lowStock || false,
    links: data?.links || [],
    attributes:
      data?.attributes?.map((attr: any) => ({
        label: attr.label || '',
        name: attr.name || '',
        roles: attr.roles || [],
        value: attr.value ?? null,
      })) || [],
    images:
      data?.images?.map((image: any) => ({
        label: image.label || '',
        roles: image.roles || [],
        url: image.url?.replace(/^https?:\/\//, '//') || '',
      })) || [],
    description: data?.description || '',
    externalId: data?.externalId || '',
    inputOptions: data?.inputOptions || [],
    addToCartAllowed: data?.addToCartAllowed || false,
    price: data.price
      ? {
          final: {
            amount: {
              value: data?.price?.final?.amount?.value || 0,
              currency: getCurrency(data?.price?.final?.amount?.currency),
            },
          },
          regular: {
            amount: {
              value: data?.price?.regular?.amount?.value || 0,
              currency: getCurrency(data?.price?.regular?.amount?.currency),
            },
          },
          roles: data?.price?.roles || [],
        }
      : undefined,
    priceRange: data?.priceRange
      ? {
          minimum: {
            final: {
              amount: {
                value: data?.priceRange?.minimum?.final?.amount?.value || 0,
                currency: getCurrency(data?.priceRange?.minimum?.final?.amount?.currency),
              },
            },
            regular: {
              amount: {
                value: data?.priceRange?.minimum?.regular?.amount?.value || 0,
                currency: getCurrency(data?.priceRange?.minimum?.regular?.amount?.currency),
              },
            },
          },
          maximum: {
            final: {
              amount: {
                value: data?.priceRange?.maximum?.final?.amount?.value || 0,
                currency: getCurrency(data?.priceRange?.maximum?.final?.amount?.currency),
              },
            },
            regular: {
              amount: {
                value: data?.priceRange?.maximum?.regular?.amount?.value || 0,
                currency: getCurrency(data?.priceRange?.maximum?.regular?.amount?.currency),
              },
            },
          },
        }
      : undefined,
    inStock: data?.inStock || false,
    typename: data?.__typename || '',
  };

  return merge(
    transformedProduct, // default transformer
    config.getConfig().models?.Product?.transformer?.(data), // custom transformer
  )
};
